import { Alert, Badge, Button, Card, Group, Modal, Stack, Switch, Table, Text, TextInput, Title } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconPlus, IconPrinter, IconWifi, IconWifiOff } from "@tabler/icons-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { http } from "../../api/http";
import { useT } from "../../i18n/script";
import { errMsg } from "../cases/common";

interface Scanner { id?: number; name: string; url: string; location?: string | null; verify_tls: boolean; active: boolean; defaults?: Record<string, unknown> }
interface Status { online: boolean; error?: string; model?: string; state?: string; sources?: string[]; resolutions?: number[] }

function StatusCell({ id }: { id: number }) {
  const { t } = useT();
  const q = useQuery({ queryKey: ["scanner-status", String(id)], queryFn: () => http.get<Status>(`/api/v1/scan/scanners/${id}/status`) });
  if (q.isLoading) return <Text size="xs" c="dimmed">…</Text>;
  const s = q.data;
  if (!s?.online) return <Group gap={4}><IconWifiOff size={14} color="var(--mantine-color-red-6)" /><Text size="xs" c="red">{s?.error ?? t("nedostupan")}</Text></Group>;
  return (
    <Group gap={4}><IconWifi size={14} color="var(--mantine-color-green-6)" />
      <Text size="xs">{s.model ?? ""} · {s.state} · {(s.sources ?? []).map((x) => t(x === "Feeder" ? "uvlakač" : "staklo")).join(", ")}</Text></Group>
  );
}

/** Mrežni skeneri (eSCL / AirScan) za skeniranje dokumentacije u predmetima. */
export function ScannersPanel() {
  const { t } = useT();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["scanners-admin"], queryFn: () => http.get<(Scanner & { id: number })[]>("/api/v1/scan/scanners", { all: true }) });
  const [edit, setEdit] = useState<Scanner | null>(null);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!edit) return;
    setBusy(true);
    try {
      if (edit.id) await http.put(`/api/v1/scan/scanners/${edit.id}`, edit); else await http.post("/api/v1/scan/scanners", edit);
      qc.invalidateQueries({ queryKey: ["scanners-admin"] }); qc.invalidateQueries({ queryKey: ["scanners"] });
      setEdit(null);
    } catch (e) { notifications.show({ color: "red", message: errMsg(e) }); } finally { setBusy(false); }
  };
  const remove = async (id: number) => {
    try { await http.del(`/api/v1/scan/scanners/${id}`); qc.invalidateQueries({ queryKey: ["scanners-admin"] }); setEdit(null); }
    catch (e) { notifications.show({ color: "red", message: errMsg(e) }); }
  };
  return (
    <Card withBorder>
      <Group justify="space-between" mb="xs">
        <Group gap="xs"><IconPrinter size={18} /><Title order={5}>{t("Skeneri za skeniranje dokumentacije")}</Title></Group>
        <Button size="xs" leftSection={<IconPlus size={14} />} onClick={() => setEdit({ name: "", url: "http://", location: "", verify_tls: false, active: true })}>{t("Novi skener")}</Button>
      </Group>
      <Text size="sm" c="dimmed" mb="sm">
        {t("Mrežni skener ili multifunkcijski uređaj sa podrškom za eSCL (AirScan / Mopria): adresa je obično http://<IP uređaja>/eSCL. Server aplikacije mora imati pristup uređaju (ista mreža ili VPN). Skener na USB-u: službenik skenira u PDF (npr. NAPS2) i bira „Fajlovi“ u čarobnjaku.")}
      </Text>
      {!q.data?.length ? <Alert variant="light" color="gray">{t("Nema podešenih skenera — skeniranje radi kamerom i fajlovima.")}</Alert> : (
        <Table striped highlightOnHover>
          <Table.Thead><Table.Tr><Table.Th>{t("Naziv")}</Table.Th><Table.Th>{t("Lokacija")}</Table.Th><Table.Th>{t("Adresa")}</Table.Th><Table.Th>{t("Stanje")}</Table.Th></Table.Tr></Table.Thead>
          <Table.Tbody>
            {q.data.map((s) => (
              <Table.Tr key={s.id} style={{ cursor: "pointer" }} onClick={() => setEdit(s)}>
                <Table.Td>{s.name} {!s.active && <Badge size="xs" color="gray">{t("neaktivan")}</Badge>}</Table.Td>
                <Table.Td>{s.location ?? "—"}</Table.Td>
                <Table.Td><Text size="xs" ff="monospace">{s.url}</Text></Table.Td>
                <Table.Td>{s.active ? <StatusCell id={s.id} /> : "—"}</Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}
      {edit && (
        <Modal opened onClose={() => setEdit(null)} title={edit.id ? edit.name : t("Novi skener")}>
          <Stack>
            <TextInput label={t("Naziv")} required value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.currentTarget.value })} placeholder="HP LaserJet MFP — šalter" />
            <TextInput label={t("eSCL adresa")} required value={edit.url} onChange={(e) => setEdit({ ...edit, url: e.currentTarget.value })} placeholder="http://10.10.1.50/eSCL" />
            <TextInput label={t("Lokacija")} value={edit.location ?? ""} onChange={(e) => setEdit({ ...edit, location: e.currentTarget.value })} placeholder={t("Područna jedinica Banja Luka, šalter 2")} />
            <Switch label={t("Provjeravaj TLS sertifikat (isključiti za https sa samopotpisanim sertifikatom uređaja)")} checked={edit.verify_tls}
              onChange={(e) => setEdit({ ...edit, verify_tls: e.currentTarget.checked })} />
            <Switch label={t("Aktivan")} checked={edit.active} onChange={(e) => setEdit({ ...edit, active: e.currentTarget.checked })} />
            <Group justify="space-between">
              {edit.id ? <Button color="red" variant="subtle" onClick={() => remove(edit.id!)}>{t("Obriši")}</Button> : <span />}
              <Button loading={busy} disabled={edit.name.length < 2 || !/^https?:\/\/[^\s/]+/.test(edit.url)} onClick={save}>{t("Sačuvaj")}</Button>
            </Group>
          </Stack>
        </Modal>
      )}
    </Card>
  );
}
